import { useEffect, useState } from 'react';
import wooCommerceApi from '../integrations/woocommerce/api';
import type { NavItem } from '../types/navigation.types';
import { NAV_ITEMS } from '../utils/constants/navigation.constants';

interface WooNavigationCategory {
  id: number;
  name: string;
  slug: string;
  parent: number;
  count: number;
}

let navigationCache: NavItem[] | null = null;
let navigationRequest: Promise<NavItem[]> | null = null;

const categoryHref = (slug: string) => `/product-category/${slug}`;

const mainCategoryMatchers = [
  /laptop/i,
  /desktop|computer system/i,
  /component/i,
  /accessor/i,
  /printer|scanner/i,
  /projector/i,
  /cctv|security camera/i,
  /pos(?:\s|-)system/i,
  /software/i,
  /used(?:\s|-)item/i,
];

const isApprovedMainCategory = (category: WooNavigationCategory): boolean => {
  const identity = `${category.slug} ${category.name}`;
  return mainCategoryMatchers.some((matcher) => matcher.test(identity));
};

const mainCategoryRank = (category: WooNavigationCategory): number => {
  const identity = `${category.slug} ${category.name}`;
  const rank = mainCategoryMatchers.findIndex((matcher) => matcher.test(identity));
  return rank === -1 ? mainCategoryMatchers.length : rank;
};

const fetchAllCategories = async (): Promise<WooNavigationCategory[]> => {
  const firstPage = await wooCommerceApi.get<WooNavigationCategory[]>('/products/categories', {
    params: { per_page: 100, page: 1, hide_empty: false, orderby: 'name', order: 'asc' },
  });
  const totalPages = Math.max(1, Number(firstPage.headers?.['x-wp-totalpages']) || 1);

  if (totalPages === 1) return firstPage.data || [];

  const remainingPages = await Promise.all(
    Array.from({ length: totalPages - 1 }, (_, index) =>
      wooCommerceApi.get<WooNavigationCategory[]>('/products/categories', {
        params: { per_page: 100, page: index + 2, hide_empty: false, orderby: 'name', order: 'asc' },
      })
    )
  );

  return [firstPage.data || [], ...remainingPages.map((response) => response.data || [])].flat();
};

const buildNavigation = (categories: WooNavigationCategory[]): NavItem[] => {
  const visible = categories.filter(
    (category) => category.slug !== 'uncategorized' && category.name && category.slug
  );
  const byParent = new Map<number, WooNavigationCategory[]>();

  visible.forEach((category) => {
    const siblings = byParent.get(category.parent) || [];
    siblings.push(category);
    byParent.set(category.parent, siblings);
  });

  const collectDescendants = (parentId: number): WooNavigationCategory[] =>
    (byParent.get(parentId) || []).flatMap((category) => [category, ...collectDescendants(category.id)]);

  const parents = (byParent.get(0) || [])
    .filter((category) => {
      const hasChildren = (byParent.get(category.id) || []).length > 0;
      return category.slug !== 'offers' && (isApprovedMainCategory(category) || hasChildren);
    })
    .sort((a, b) => mainCategoryRank(a) - mainCategoryRank(b) || a.name.localeCompare(b.name));

  const dynamicItems: NavItem[] = parents.map((category) => {
    const childItems = collectDescendants(category.id)
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((child) => ({
        label: child.name,
        slug: child.slug,
        href: categoryHref(child.slug),
      }));

    return {
      label: category.name.replace(/&amp;/gi, '&'),
      slug: category.slug,
      basePath: categoryHref(category.slug),
      ...(childItems.length ? { children: childItems } : {}),
    };
  });

  return [
    ...dynamicItems,
    { label: 'OFFERS', basePath: '/offers', hotBadge: true },
  ];
};

const loadNavigation = async (): Promise<NavItem[]> => {
  if (navigationCache) return navigationCache;
  if (navigationRequest) return navigationRequest;

  navigationRequest = fetchAllCategories()
    .then((categories) => {
      const generated = buildNavigation(categories);
      navigationCache = generated.length > 1 ? generated : NAV_ITEMS;
      return navigationCache;
    })
    .catch(() => NAV_ITEMS)
    .finally(() => {
      navigationRequest = null;
    });

  return navigationRequest;
};

export const useWooCategoryNavigation = (): NavItem[] => {
  const [items, setItems] = useState<NavItem[]>(navigationCache || NAV_ITEMS);

  useEffect(() => {
    let active = true;
    loadNavigation().then((loadedItems) => {
      if (active) setItems(loadedItems);
    });
    return () => {
      active = false;
    };
  }, []);

  return items;
};
